import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../Configurations/AuthContext";
import { showMenu } from "./CompanyMenu";
import { isPlanExpired } from "./dateUtils";
import DefaultData from "../../CompanyData/DefaultData";

const ComingSoonItem = ({ icon, label }) => {
  const disabledLinkStyle = {
    display: "flex",
    alignItems: "center",
    padding: "0.6rem 1.1rem",
    borderRadius: "0.5rem",
    textDecoration: "none",
    color: DefaultData.THEMECOLOR3,
    fontWeight: "500",
    cursor: "not-allowed",
    position: "relative",
  };

  const badgeStyle = {
    position: "absolute",
    top: "-5px",
    right: "-5px",
    fontSize: "0.6rem",
    fontWeight: "bold",
    padding: "0.1rem 0.4rem",
    borderRadius: "1rem",
    backgroundColor: DefaultData.THEMECOLOR2,
    color: DefaultData.THEMECOLOR3,
  };

  return (
    <div style={disabledLinkStyle}>
      <i className={`${icon} me-2`}></i>
      <span>{label}</span>
      <span style={badgeStyle}>SOON</span>
    </div>
  );
};

export default function Header({ activeSection }) {
  const { user, loading } = useAuth();
  const planIsExpired = user ? isPlanExpired(user.endDate) : false;

  if (loading || !user) {
    return (
      <div
        className="main-header"
        style={{
          minHeight: "60px",
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #DEE2E6",
        }}
      ></div>
    );
  }

  const navLinkStyle = {
    display: "flex",
    alignItems: "center",
    padding: "0.6rem 1.1rem",
    borderRadius: "0.5rem",
    textDecoration: "none",
    color: DefaultData.THEMECOLOR3,
    fontWeight: "500",
    transition: "background-color 0.2s ease, color 0.2s ease",
  };

  const activeNavLinkStyle = {
    ...navLinkStyle,
    backgroundColor: DefaultData.THEMECOLOR3,
    color: DefaultData.THEMECOLOR2,
  };

  // UPDATED: Now only checks against the activeSection prop
  const getStyle = (sectionName) => {
    return activeSection === sectionName ? activeNavLinkStyle : navLinkStyle;
  };

  // Define special company types
  const specialCompanyTypes = ["VISA", "SERIESFAIR", "DMC", "HOTEL", "SUPPLIER"];
  const isSpecialCompanyType = specialCompanyTypes.includes(user.companyType);
  
  return (
    <div
      className="main-header"
      style={{
        backgroundColor: DefaultData.THEMECOLOR1,
        borderBottom: "1px solid #DEE2E6",
        padding: "0.5rem 0.5rem",
        position: "sticky",
        top: "70px",
        zIndex: 1020,
        minHeight: "60px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div className="container-fluid">
        {planIsExpired ? (
          <div className="ticker-wrap">
            <div className="ticker-move fw-bold text-danger">
              To access our full suite of services... please subscribe to a plan.
            </div>
          </div>
        ) : (
          <>
            {isSpecialCompanyType ? (
              // Special navigation for VISA, SERIESFAIR, PACKAGE, HOTEL, and SUPPLIER company types
              <nav className="d-flex align-items-center gap-1">
                {/* HOTEL company type - display directly in header */}
                {user.companyType === "HOTEL" && showMenu("hotels") && (
                  <>
                    <Link to={"/hoteldashboard"} style={getStyle("HOTELDASHBOARD")}>
                      <i className="ri-home-4-line me-2"></i>
                      <span>Dashboard</span>
                    </Link>
                    <Link to={"/myproperties"} style={getStyle("HOTELPROPERTIES")}>
                      <i className="ri-building-line me-2"></i>
                      <span>My Properties</span>
                    </Link>
                    <Link to={"/hotels/search"} style={getStyle("MYHOTELS")}>
                      <i className="ri-hotel-line me-2"></i>
                      <span>My Hotels</span>
                    </Link>
                    
                   
                  </>
                )}
                
                {/* PACKAGE/DMC company type - display directly in header */}
                {user.companyType === "DMC" && showMenu("packages") && (
                  <>
                    <Link to={"/holidaydashboard"} style={getStyle("HOLIDAYDASHBOARD")}>
                      <i className="ri-home-4-line me-2"></i>
                      <span>Dashboard</span>
                    </Link>
                    
                    <Link to={"/dmcpackagecreate"} style={getStyle("DMCPACKAGECREATE")}>
                      <i className="ri-add-circle-line me-2"></i>
                      <span>Create Package</span>
                    </Link>
                    
                    <Link to={"/dmcpackageslist"} style={getStyle("DMCPACKAGESLIST")}>
                      <i className="ri-luggage-deposit-line me-2"></i>
                      <span>Packages</span>
                    </Link>
                    
                    <Link to={"/dmcpackageenquirylist"} style={getStyle("DMCPACKAGEENQUIRYLIST")}>
                      <i className="ri-questionnaire-line me-2"></i>
                      <span>Enquiries</span>
                    </Link>
                    
                
                  </>
                )}
                
                {/* SUPPLIER specific navigation */}
                {user.companyType === "SUPPLIER" && (
                  <>
                    <Link to="/holidaydashboard" style={getStyle("holidays")}>
                      <i className="ri-home-4-line me-2"></i>
                      <span>Holidays</span>
                    </Link>

                    <Link to="/visadashboard" style={getStyle("visas")}>
                      <i className="ri-passport-line me-2"></i>
                      <span>Visas</span>
                    </Link>

                    <Link to="/leaddashboard" style={getStyle("leads")}>
                      <i className="ri-message-3-line me-2"></i>
                      <span>Leads</span>
                    </Link>

                    <Link to="/seriesfare/dashboard" style={getStyle("seriesfares")}>
                      <i className="ri-flight-takeoff-line me-2"></i>
                      <span>Series Fares</span>
                    </Link>
                  </>
                )}
                
                {/* VISA company type - display directly in header */}
                {user.companyType === "VISA" && showMenu("visas") && (
                  <>
                    <Link to={"/visadashboard"} style={getStyle("VISADASHBOARD")}>
                      <i className="ri-passport-line me-2"></i>
                      <span>Dashboard</span>
                    </Link>
                    
                    <Link to={"/supplier/visaprices"} style={getStyle("PRICES")}>
                      <i className="ri-money-dollar-circle-line me-2"></i>
                      <span>Prices</span>
                    </Link>
                    
                    <Link to={"/supplier/visarequests"} style={getStyle("REQUESTS")}>
                      <i className="ri-message-3-line me-2"></i>
                      <span>Requests</span>
                    </Link>
                    
                    <Link to={"/supplier/visaappointments"} style={getStyle("APPOINTMENTS")}>
                      <i className="ri-calendar-check-line me-2"></i>
                      <span>Appointments</span>
                    </Link>
                  </>
                )}
                
                {/* SERIESFAIR company type - display directly in header */}
                {user.companyType === "SERIESFAIR" && showMenu("flights") && (
                  <>
                    <Link to={"/seriesfare/dashboard"} style={getStyle("SERIESFAREDASHBOARD")}>
                      <i className="ri-home-4-line me-2"></i>
                      <span>Dashboard</span>
                    </Link>
                    
                    <Link to={"/seriesfare"} style={getStyle("SERIESFARECREATE")}>
                      <i className="ri-add-circle-line me-2"></i>
                      <span>Create Series Fare  </span>
                    </Link>
                    
                    <Link to={"/seriesfarelist"} style={getStyle("SERIESFARELISTS")}>
                      <i className="ri-list-check-2 me-2"></i>
                      <span>Lists</span>
                    </Link>
                    
                    <Link to={"/seriesfare/blockrequest"} style={getStyle("SERIESFAREREQUESTS")}>
                      <i className="ri-message-3-line me-2"></i>
                      <span>Requests</span>
                    </Link>
                  </>
                )}
                
                {/* Always show Leads for all special company types except SUPPLIER which already has it */}
                {user.companyType !== "SUPPLIER" && (
                  <Link to={"/leaddashboard"} style={getStyle("leads")}>
                    <i className="ri-message-3-line me-2"></i>
                    <span>Leads</span>
                  </Link>
                )}
                
                {/* Always show Accounts for all special company types */}
                <Link to={"/accounts-dashboard"} style={getStyle("accounts")}>
                  <i className="ri-money-dollar-circle-line me-2" />
                  <span>Accounts</span>
                </Link>

              </nav>

            ) : (
              // Original navigation for other company types (with Dashboard)
              <nav className="d-flex align-items-center gap-1">
                {showMenu("dashboard") && (
                  <Link to={"/dashboard"} style={getStyle("dashboard")}>
                    <i className="ri-home-4-line me-2"></i>
                    <span>Dashboard</span>
                  </Link>
                )}
                {showMenu("marketing") && (
                  <Link to={"/leaddashboard"} style={getStyle("leads")}>
                    <i className="ri-message-3-line me-2"></i>
                    <span>Leads</span>
                  </Link>
                )}
                {showMenu("flights") && (
                  <Link to={"/flight/search"} style={getStyle("flights")} >
                    <i className="ri-flight-takeoff-line me-2"></i>
                    <span>Flights</span>
                  </Link>
                )}
                {showMenu("hotels") && (
                  <Link to={"/hotel/search"} style={getStyle("hotels")}>
                    <i className="ri-building-line me-2"></i>
                    <span>Hotels</span>
                  </Link>
                )}
                {showMenu("packages") && (
                  <Link to={"/package"} style={getStyle("packages")}>
                    <i className="ri-luggage-deposit-line me-2"></i>
                    <span>Packages</span>
                  </Link>
                )}
                {showMenu("sightseeing") && (
                  <Link to={"/sightseeing/search"} style={getStyle("sightseeing")}>
                    <i className="ri-map-pin-line me-2"></i>
                    <span>SightSeeing</span>
                  </Link>
                )}
                {showMenu("visas") && (
                  <Link to={"/visas"} style={getStyle("visas")}>
                    <i className="ri-passport-line me-2"></i>
                    <span>Visas</span>
                  </Link>
                )}
                {showMenu("buses") && (
                  <Link to={"/bus/search"} style={getStyle("buses")}>
                    <i className="ri-bus-line me-2"></i>
                    <span>Buses</span>
                  </Link>
                )}
                {showMenu("dmcs") && (
                  <Link to={"/dmc-list"} style={getStyle("dmcs")}>
                    <i className="ri-building-4-line  me-2" />
                    <span>DMCs</span>
                  </Link>
                )}
                {showMenu("accounts") && (
                  <Link to={"/accounts-dashboard"} style={getStyle("accounts")}>
                    <i className="ri-money-dollar-circle-line  me-2" />
                    <span>Accounts</span>
                  </Link>
                )}
                {showMenu("reports") && (
                  <Link to={"/flightreport"} style={getStyle("reports")}>
                    <i className="ri-file-text-line  me-2" />
                    <span>Reports</span>
                  </Link>
                )}
               
                <ComingSoonItem icon="ri-car-line" label="Cars" />
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
}